import { useQuery } from '@tanstack/react-query'

import { assessmentProcessService } from '@/services/api/assessment_process.service'
import type { AssessmentProcessTree } from '@/types/assessment_process.types'
import { assessmentProcessQueryKeys } from '@/utils/query/assessments/assessmentProcessQueryClient.utils'
import { defaultQueryOptions, errorUtils } from '@/utils/query/common'

export interface UseAssessmentProcessOptions {
  assessmentId?: string
  enabled?: boolean
}

export interface UseAssessmentProcessReturn {
  assessmentProcess: AssessmentProcessTree | null
  isLoading: boolean
  isError: boolean
  error: string | null
  refetch: () => void
  isFetching: boolean
}

export function useAssessmentProcess(
  options: UseAssessmentProcessOptions = {}
): UseAssessmentProcessReturn {
  const { assessmentId, enabled = true } = options

  const query = useQuery({
    queryKey: assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId!),
    queryFn: () => assessmentProcessService.getAssessmentProcessById(assessmentId!),
    enabled: enabled && !!assessmentId,
    ...defaultQueryOptions,
    staleTime: 1000 * 60 * 2, // 2 minutes - process data changes less frequently
    gcTime: 1000 * 60 * 10 // 10 minutes cache time
  })

  return {
    assessmentProcess: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ? errorUtils.transformAxiosError(query.error) : null,
    refetch: query.refetch,
    isFetching: query.isFetching
  }
}
